
/**
 * Device permissions and initialization for Twilio calls
 */

export type PermissionState = 'granted' | 'denied' | 'prompt';

/**
 * Check current microphone permission status without requesting access
 */
export const checkMicrophonePermission = async (): Promise<PermissionState> => {
  try {
    if (!navigator.permissions) {
      console.warn('Permissions API not supported');
      return 'prompt';
    }
    
    const permission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    console.log('🎤 Microphone permission status:', permission.state);
    return permission.state as PermissionState;
  } catch (error) {
    console.error('Error checking microphone permission:', error);
    return 'prompt';
  }
};

/**
 * Request microphone permission only if not already granted
 */
export const requestMicrophonePermission = async (): Promise<boolean> => {
  try {
    // First check existing permission state
    const currentPermission = await checkMicrophonePermission();
    
    if (currentPermission === 'granted') {
      console.log('🎤 Microphone already granted, skipping request');
      return true;
    }
    
    if (currentPermission === 'denied') {
      console.warn('🎤 Microphone previously denied');
      return false;
    }
    
    console.log('🎤 Requesting microphone permission...');
    
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    console.log('✅ Microphone access granted');
    
    // Stop the stream immediately after getting permission
    stream.getTracks().forEach(track => track.stop());
    
    return true;
  } catch (error: any) {
    console.error('❌ Microphone access denied:', error);
    return false;
  }
};

/**
 * Initialize Twilio devices and request permissions only if needed
 * @returns Promise with initialization result
 */
export const initializeDevices = async () => {
  try {
    console.log('🔧 Initializing devices...');
    
    // Check if getUserMedia is supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('getUserMedia is not supported in this browser');
    }
    
    // Check current permission before requesting
    const currentPermission = await checkMicrophonePermission();
    
    if (currentPermission === 'granted') {
      console.log('🔧 Microphone already granted, device initialization successful');
      return {
        success: true,
        error: null,
      };
    }
    
    // Only request if not already granted
    const granted = await requestMicrophonePermission();
    
    if (granted) {
      console.log('✅ Device initialization successful');
      return {
        success: true,
        error: null,
      };
    } else {
      return {
        success: false,
        error: 'Microphone permission denied',
      };
    }
  } catch (error: any) {
    console.error('❌ Error initializing devices:', error);
    return {
      success: false,
      error: error.message || 'Failed to initialize devices',
    };
  }
};
